<script lang="ts">
    import type { ApiRequestParams, ApiReturnType, ApiSchemas } from '$lib/api/shopwareApi.js';
    import { formatCurrency } from '$lib/util/currencyUtil.js';
    import VDPLogoSVG from '$lib/assets/icons/VDPLogoSVG.svelte';
    import { number } from 'svelte-i18n';
    import CustomSelect from '$lib/components/util/CustomSelect.svelte';
    import { t } from 'svelte-i18n';
    import Button from '$lib/daisyUiComponents/ButtonVV.svelte';
    import { useLocaleContext, useShopContext, useStoreContext } from '$lib/context.js';
    import { createMutation, useQueryClient } from '@tanstack/svelte-query';
    import { CartApi } from '$lib/api/cartApi.js';
    import type { Selected } from 'bits-ui';
    import { executeAddToCart } from '$lib/util/cartUtil.js';
    import R2Image from '$lib/components/util/R2Image.svelte';

    const lang = useLocaleContext();
    const shopContext = useShopContext();
    const queryClient = useQueryClient();
    const storeContext = useStoreContext();

    export let product: ApiSchemas['Product'];
    export let cart: ApiSchemas['Cart'];

    const addToCartMutation = createMutation<ApiReturnType<'addLineItem'>, unknown, ApiRequestParams<'addLineItem'>>({
        mutationFn: (data) => CartApi.addLineItem(data, shopContext),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cart'] })
    });

    const updateCartMutation = createMutation<
        ApiReturnType<'updateLineItem'>,
        unknown,
        ApiRequestParams<'updateLineItem'>
    >({
        mutationFn: (data) => CartApi.updateLineItem(data, shopContext),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cart'] })
    });

    let quantity: Selected<number> = { value: 1, label: '1' };

    async function addToCart() {
        await executeAddToCart(
            product.id,
            cart,
            $addToCartMutation,
            $updateCartMutation,
            {
                productName: product.name ?? product.translated?.name
            },
            quantity.value
        );
    }
</script>

{#if product}
    <div
        class="relative gap-[0.9375rem] sm:gap-0 flex w-[calc(100vw-40px)] flex-col pb-0 mb-20 h-auto max-w-[339px] items-center"
    >
        <!-- TODO: Optional check for VDP or GG (?) -->
        {#if (product.name ?? product.translated.name).includes('®')}
            <div class="hidden sm:block absolute right-2 top-0 w-7 h-9 z-10 text-base-200">
                <VDPLogoSVG width="28" />
            </div>
        {/if}
        <a
            href="/{lang}/product/{product.productNumber}"
            class="sm:max-w-full min-w-[6rem] w-full sm:min-w-full sm:w-full sm:h-auto flex justify-center items-end overflow-hidden"
        >
            {#if product.cover?.media}
                <div class="h-48 w-full">
                    <div class="relative w-full h-full group">
                        <R2Image
                            widths={[500]}
                            imageId={product.cover.media.url}
                            alt={product.cover.media.alt}
                            loading="eager"
                            decoding="sync"
                            class="object-cover h-full w-full transition-transform duration-500 sm:hover:scale-110"
                        />
                        <div
                            class="block absolute top-0 right-[20%] w-0 border-r-2 border-base-100 h-full border-dashed"
                        />
                    </div>
                </div>
            {/if}
        </a>
        <div class="sm:w-full flex flex-col 2xl:mb-0 sm:mt-8 flex-grow justify-end sm:justify-start">
            {#if (product.name ?? product.translated.name).includes('®')}
                <div class="sm:hidden block mb-2 w-6 2xl:w-7 text-base-200">
                    <VDPLogoSVG width="28" />
                </div>
            {/if}
            <h3 class="line-clamp-1 text-xs 2xl:text-sm text-classic-grey font-medium tracking-[0.1em] mb-0.5">
                EventTicket
            </h3>
            <a
                href="/{lang}/product/{product.productNumber}"
                class="line-clamp-2 min-h-[2.5em] text-[1.375rem] sm:text-[1.75rem] leading-[1.25em] tracking-[0.05em] font-light mb-2.5 grow"
            >
                {@html (product.name ?? product.translated.name).replace(/®/g, '<sup>®</sup>')}
            </a>
            <div class="flex justify-between items-end sm:flex-col mt-[1.125rem] 2xl:-mt-4">
                <p
                    class="text-[1.3125rem] 2xl:text-2xl font-medium tracking-[0.07em] text-primary flex flex-col items-end mt-8"
                >
                    {#if product?.calculatedPrice?.listPrice}
                        <span class="text-accent text-sm line-through"
                            >{formatCurrency($number, storeContext, product?.calculatedPrice?.listPrice.price)}</span
                        >
                    {/if}
                    {formatCurrency($number, storeContext, product?.calculatedPrice?.unitPrice)}
                </p>
            </div>
            <div class="flex items-end gap-2.5 mt-2">
                <div class="select-border">
                    <CustomSelect
                        class="select"
                        bind:selected={quantity}
                        options={Array.from({ length: product?.calculatedMaxPurchase }, (_, i) => i + 1)}
                    />
                </div>
                <div class="select-border grow">
                    <Button
                        disabled={!product?.available}
                        color="primary"
                        on:click={addToCart}
                        loading={$addToCartMutation.isPending || $updateCartMutation.isPending}
                        class="w-full"
                        innerClasses="!px-0"
                    >
                        <div class="flex sm:hidden gap-1.5">
                            <span class="-translate-y-[1px]">+</span>
                            {$t('shop.scrollable-list.add-to-cart-mobile')}
                        </div>

                        <div class="hidden sm:block">
                            {$t('shop.scrollable-list.add-to-cart')}
                        </div>
                    </Button>
                </div>
            </div>
        </div>
    </div>
{/if}
